import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Envelope, Lock, Key, ArrowLeft, CheckCircle, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: Enter Email, 2: Enter OTP & New Password
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [demoOtp, setDemoOtp] = useState(''); // For demo mode
  const navigate = useNavigate();

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success(response.data.message);
      
      // Store demo OTP if returned (remove in production)
      if (response.data.demo_otp) {
        setDemoOtp(response.data.demo_otp);
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        otp,
        new_password: newPassword
      });
      toast.success(response.data.message);
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-orange-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTMwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHpNNiAzNGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
      </div>

      {/* Floating blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="border-2 border-orange-500 bg-white shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-purple-500/5"></div>
          
          <CardContent className="relative pt-8 pb-8 px-6 md:px-10">
            {/* Logo */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 mb-4">
                <Sparkle className="h-6 w-6 text-orange-500" weight="fill" />
                <span className="font-['Chivo'] text-xl font-black text-orange-600">Designbiz.in</span>
              </div>
            </div>

            {step === 1 ? (
              <>
                {/* Step 1: Enter Email */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Key className="h-8 w-8 text-orange-600" weight="duotone" />
                  </div>
                  <h3 className="font-['Chivo'] text-2xl font-black text-gray-900 mb-2">
                    Password विसरलात?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    तुमचा registered email टाका. आम्ही तुम्हाला OTP पाठवू.
                  </p>
                </div>

                <form onSubmit={handleRequestOTP} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-900">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                      <Input
                        id="email"
                        data-testid="forgot-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="send-otp-button"
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white font-bold rounded-full h-12 text-base shadow-xl hover:shadow-2xl transition-all"
                    disabled={loading}
                  >
                    {loading ? 'Sending OTP...' : 'OTP पाठवा'}
                  </Button>
                </form>
              </>
            ) : (
              <>
                {/* Step 2: Enter OTP & New Password */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" weight="duotone" />
                  </div>
                  <h3 className="font-['Chivo'] text-2xl font-black text-gray-900 mb-2">
                    OTP Verify करा
                  </h3>
                  <p className="text-gray-600 text-sm">
                    <span className="font-medium text-orange-600">{email}</span> वर OTP पाठवला आहे
                  </p>
                  
                  {/* Demo OTP Display - REMOVE IN PRODUCTION */}
                  {demoOtp && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-700 font-medium">Demo Mode - OTP:</p>
                      <p className="text-2xl font-black text-yellow-800 tracking-widest">{demoOtp}</p>
                    </div>
                  )}
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-sm font-semibold text-gray-900">
                      OTP Code
                    </Label>
                    <Input
                      id="otp"
                      data-testid="otp-input"
                      type="text"
                      placeholder="6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-12 border-2 border-gray-200 focus:border-orange-500 transition-all text-center text-2xl font-bold tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-semibold text-gray-900">
                      New Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                      <Input
                        id="newPassword"
                        data-testid="new-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-11 h-12 border-2 border-gray-200 focus:border-orange-500 transition-all"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-900">
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" weight="duotone" />
                      <Input
                        id="confirmPassword"
                        data-testid="confirm-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`pl-11 h-12 border-2 transition-all ${
                          confirmPassword && newPassword !== confirmPassword 
                            ? 'border-red-500 focus:border-red-500' 
                            : 'border-gray-200 focus:border-orange-500'
                        }`}
                        required
                      />
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    data-testid="reset-password-button"
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-full h-12 text-base shadow-xl hover:shadow-2xl transition-all"
                    disabled={loading || newPassword !== confirmPassword}
                  >
                    {loading ? 'Resetting...' : 'Password Reset करा'}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full text-sm text-gray-600 hover:text-orange-600 flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    वेगळा email वापरा
                  </button>
                </form>
              </>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/auth"
                className="text-sm text-gray-600 hover:text-orange-600 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Login page वर परत जा
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
